// Route: /tools/essay/[id] — детальная страница эссе: текст с inline-аннотациями
// + панель критериев + апелляция + экспорт PDF.
// URL state: ?demo=loading|error|processing|graded|partial
//
// См. docs/ui-spec/essays.[id].{desktop,mobile}.md
//
// Тактильный редизайн «Картотека»: текст эссе — elevated-панель e2 с serif-
// типографикой и кликабельными аннотациями; критерии — sticky-колонка с
// тактильным dial-итогом; баннеры состояний (ручная проверка / AI-detection /
// апелляция) — тоновые well-карточки; кнопки — тактильные клавиши.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import {
	Button,
	EmptyState,
	ErrorState,
	FIT_APP_PAGE_SHELL,
	LIST_PAGE_SHELL,
	type LoaderStep,
	MagicMomentLoader,
	Skeleton,
	STATUS_PAGE_SHELL,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconAlertTriangle,
	IconArrowLeft,
	IconCircleCheckFilled,
	IconFlag,
	IconRefresh,
	IconShieldCheck,
	IconThumbUp,
	IconTrendingUp,
} from "@tabler/icons-react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { EssayDetailDto } from "@/components/essays/_api";
import { AnnotatedText } from "@/components/essays/annotated-text";
import { CriteriaSidebar } from "@/components/essays/criteria-sidebar";
import { EditableEssayTitle } from "@/components/essays/editable-essay-title";
import { ExportPdfButton } from "@/components/essays/export-pdf-button";
import { RegenerateAnnotationsButton } from "@/components/essays/regenerate-annotations-button";
import { OfflineDetailState } from "@/components/shared/offline-detail-state";
import { RichContent } from "@/components/shared/rich-content";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { useEssayRealtimeSync } from "@/hooks/use-essay-realtime-sync";
import { useEssayDetailLegacy, useRegradeEssay } from "@/hooks/use-essays";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useSocketGenerationProgress } from "@/hooks/use-socket-generation-progress";
import { isOfflineWithoutCache } from "@/lib/offline-query-state";
import { useIsLgUp } from "@/lib/platform-detect";
import { userFacingErrorMessage } from "@/lib/user-facing-error";

const searchSchema = z.object({
	demo: z.enum(["default", "loading", "error", "processing", "graded", "partial"]).optional(),
});

export const Route = createFileRoute("/tools/essay/$id")({
	validateSearch: tolerantSearch(searchSchema),
	component: EssayDetailPage,
	pendingComponent: DetailSkeleton,
});

/** Лёгенда тонов аннотаций — помогает читать выделения смысла. */
const ANNOTATION_LEGEND = [
	{ kind: "praise", cls: "essay-mark--praise" },
	{ kind: "issue", cls: "essay-mark--issue" },
	{ kind: "suggestion", cls: "essay-mark--suggestion" },
	{ kind: "question", cls: "essay-mark--question" },
] as const;

function AnnotationLegend(): React.ReactElement {
	const { t } = useTranslation("essays");
	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-2xs text-muted-foreground">
			{ANNOTATION_LEGEND.map(({ kind, cls }) => (
				<span key={kind} className="inline-flex items-center gap-1.5">
					<span className={`essay-mark ${cls} h-3 w-5 rounded-sm`} aria-hidden="true" />
					{t(`detail.annotations.kind.${kind}`)}
				</span>
			))}
		</div>
	);
}

function EssayDetailPage(): React.ReactElement {
	const { t } = useTranslation("essays");
	const { t: tc } = useTranslation("common");
	const { id } = useParams({ from: "/tools/essay/$id" });
	const search = Route.useSearch();
	const isDesktop = useIsLgUp();
	const [focusedAnnotation, setFocusedAnnotation] = useState<string | null>(null);
	useArtifactWatching(markArtifactSeenKey("essay", id));
	useEssayRealtimeSync({ essayId: id });
	const gradingLive = useSocketGenerationProgress({
		event: "essays:grading_progress",
		match: (payload) => payload.essayId === id,
	});

	const detailQuery = useEssayDetailLegacy(id);
	const regrade = useRegradeEssay();

	const isLoading = search.demo === "loading" || detailQuery.isLoading;
	const isError = detailQuery.isError;
	const isProcessing = search.demo === "processing";
	const essay = useMemo<EssayDetailDto | undefined>(() => {
		if (!detailQuery.data) return undefined;
		const base = detailQuery.data;
		if (search.demo === "partial") return { ...base, requiresManualReview: true };
		if (search.demo === "processing") return { ...base, status: "grading", overallScore: null };
		return base;
	}, [detailQuery.data, search.demo]);

	if (isLoading) {
		return <DetailSkeleton />;
	}
	if (isError) {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<ErrorState
					title={t("detail.errorTitle")}
					description={t("list.errorSubtitle")}
					action={
						<Button asChild variant="outline">
							<Link to="/tools/essay">{t("detail.back")}</Link>
						</Button>
					}
				/>
			</div>
		);
	}
	// Офлайн без локальной копии: запрос paused, поэтому скелетон здесь висел бы
	// до восстановления сети, ничего не сообщая пользователю.
	if (isOfflineWithoutCache(detailQuery)) {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<OfflineDetailState onRetry={() => void detailQuery.refetch()} />
			</div>
		);
	}
	if (!essay) {
		return <DetailSkeleton />;
	}

	const handleRegrade = async () => {
		try {
			await regrade.mutateAsync({ essayId: id });
			toast.success(t("toast.regradeStarted"));
		} catch (err) {
			toast.error(userFacingErrorMessage(err, t) ?? t("toast.regradeError"));
		}
	};

	if (isProcessing || essay.status === "grading" || essay.status === "queued") {
		const fromDto =
			typeof essay.gradingProgress === "number"
				? essay.gradingProgress > 1
					? essay.gradingProgress / 100
					: essay.gradingProgress
				: null;
		const fallback = essay.status === "queued" ? 0.05 : 0.2;
		const progress = Math.max(gradingLive.progress, fromDto ?? fallback);
		const percent = Math.round(Math.min(1, Math.max(0, progress)) * 100);
		const steps: LoaderStep[] = [
			{
				label: t("detail.processing.steps.extract"),
				status: progress >= 0.25 ? "done" : progress > 0 ? "in-progress" : "pending",
			},
			{
				label: t("detail.processing.steps.analyze"),
				status: progress >= 0.5 ? "done" : progress >= 0.25 ? "in-progress" : "pending",
			},
			{
				label: t("detail.processing.steps.annotate"),
				status: progress >= 0.75 ? "done" : progress >= 0.5 ? "in-progress" : "pending",
			},
			{
				label: t("detail.processing.steps.score"),
				status: progress >= 0.95 ? "done" : progress >= 0.75 ? "in-progress" : "pending",
			},
		];
		return (
			<div className={STATUS_PAGE_SHELL}>
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="-ml-2 mb-4 h-8 w-fit px-2 text-muted-foreground"
				>
					<Link to="/tools/essay">
						<IconArrowLeft size={14} />
						{t("detail.back")}
					</Link>
				</Button>
				<MagicMomentLoader
					title={t("detail.processing.title")}
					steps={steps}
					tip={t("detail.processing.subtitle")}
					progress={progress}
					progressLabel={tc("generation.progress", { percent, defaultValue: `${percent}%` })}
				/>
			</div>
		);
	}
	if (essay.status === "failed") {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<EmptyState
					icon={<IconAlertTriangle size={48} className="text-destructive" />}
					title={t("detail.failedTitle")}
					description={
						essay.errorMessage
							? t(`errors.${essay.errorMessage}`, { defaultValue: essay.errorMessage })
							: t("detail.failedSubtitle")
					}
					action={
						<div className="flex flex-wrap justify-center gap-2">
							<Button onClick={() => void handleRegrade()} disabled={regrade.isPending}>
								<IconRefresh size={16} />
								{t("detail.retry")}
							</Button>
							<Button asChild variant="outline">
								<Link to="/tools/essay">{t("detail.back")}</Link>
							</Button>
						</div>
					}
				/>
			</div>
		);
	}

	// Инвариант: status='ready' ⟹ grading есть.
	if (essay.status === "ready" && essay.overallScore == null && essay.criteria.length === 0) {
		return (
			<div className={STATUS_PAGE_SHELL}>
				<EmptyState
					icon={<IconAlertTriangle size={48} className="text-warning" />}
					title={t("detail.gradingMissingTitle")}
					description={t("detail.gradingMissingSubtitle")}
					action={
						<div className="flex flex-wrap justify-center gap-2">
							<Button onClick={() => void handleRegrade()} disabled={regrade.isPending}>
								<IconRefresh size={16} />
								{t("detail.retry")}
							</Button>
							<Button asChild variant="outline">
								<Link to="/tools/essay">{t("detail.back")}</Link>
							</Button>
						</div>
					}
				/>
			</div>
		);
	}

	const hasReport = Boolean(essay.reportUrl);

	const essayPanel = (
		<>
			<div className="mb-3 flex flex-col gap-2 border-b border-border pb-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
				<h2 className="text-sm font-semibold text-foreground">{t("detail.tab.essay")}</h2>
				<AnnotationLegend />
			</div>
			<AnnotatedText
				text={essay.text}
				annotations={essay.annotations}
				criteria={essay.criteria}
				focusedAnnotationId={focusedAnnotation}
				onAnnotationFocus={setFocusedAnnotation}
			/>
		</>
	);

	const summaryPanel = (
		<>
			<h2 className="mb-2 text-sm font-semibold text-foreground">{t("detail.summary")}</h2>
			<RichContent content={essay.summary} className="text-sm leading-relaxed text-foreground/85" />
			<StrengthsImprovements essay={essay} />
		</>
	);

	const header = (
		<header className="shrink-0 space-y-3">
			<Button asChild variant="ghost" size="sm" className="-ml-2 h-8 w-fit px-2">
				<Link to="/tools/essay">
					<IconArrowLeft size={14} />
					{t("detail.back")}
				</Link>
			</Button>
			<div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
				<div className="min-w-0 space-y-1">
					<EditableEssayTitle essayId={essay.id} title={essay.title} />
					<p className="text-xs tabular-nums text-muted-foreground">
						{t("list.wordCount", { count: essay.wordCount })}
					</p>
				</div>
				<div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
					<RegenerateAnnotationsButton
						essayId={essay.id}
						className="w-full cursor-pointer sm:w-auto"
					/>
					<ExportPdfButton
						essayId={essay.id}
						hasReport={hasReport}
						className="col-span-2 w-full cursor-pointer sm:col-span-1 sm:w-auto"
					/>
				</div>
			</div>

			{/* Тоновые well-баннеры состояний */}
			{essay.requiresManualReview && (
				<StatusBanner
					tone="warning"
					icon={<IconFlag size={16} />}
					title={t("detail.manualReview")}
					subtitle={t("detail.manualReviewSubtitle")}
				/>
			)}
			{essay.aiDetectionScore != null && essay.aiDetectionScore > 0.5 && (
				<StatusBanner
					tone="info"
					icon={<IconShieldCheck size={16} />}
					title={t("detail.aiDetection", {
						percent: Math.round(essay.aiDetectionScore * 100),
					})}
				/>
			)}
		</header>
	);

	// Desktop: fit-to-viewport — шапка shrink-0, обе колонки заполняют остаток
	// и скроллятся внутри. `grid-rows-1` (= minmax(0,1fr)) обязателен: иначе
	// auto-строка тянется по max-content критериев/текста и колонка уходит
	// ниже вьюпорта (page-scroll). Тот же приём — material-workspace-page.
	if (isDesktop) {
		return (
			<div className={FIT_APP_PAGE_SHELL}>
				{header}
				<div className="grid min-h-0 flex-1 grid-cols-12 grid-rows-1 gap-6">
					<article className="col-span-7 min-h-0 overflow-y-auto overscroll-contain rounded-[var(--radius-panel)] border border-border bg-card p-6 shadow-e2 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.5),0_1px_2px_rgb(var(--shadow)/0.04),0_5px_14px_rgb(var(--shadow)/0.045)]">
						{essayPanel}
						<div className="mt-6 border-t border-border pt-5">{summaryPanel}</div>
					</article>
					<div className="col-span-5 min-h-0 overflow-y-auto overscroll-contain">
						<CriteriaSidebar essay={essay} />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={LIST_PAGE_SHELL}>
			{header}
			<Tabs defaultValue="essay" className="w-full">
				<TabsList className="w-full">
					<TabsTrigger value="essay" className="flex-1">
						{t("detail.tab.essay")}
					</TabsTrigger>
					<TabsTrigger value="criteria" className="flex-1">
						{t("detail.tab.criteria")}
					</TabsTrigger>
					<TabsTrigger value="summary" className="flex-1">
						{t("detail.tab.summary")}
					</TabsTrigger>
				</TabsList>
				<TabsContent
					value="essay"
					className="mt-4 rounded-[var(--radius-panel)] border border-border bg-card p-4 shadow-e1"
				>
					{essayPanel}
				</TabsContent>
				<TabsContent value="criteria" className="mt-4">
					<CriteriaSidebar essay={essay} />
				</TabsContent>
				<TabsContent
					value="summary"
					className="mt-4 rounded-[var(--radius-panel)] border border-border bg-card p-4 shadow-e1"
				>
					{summaryPanel}
				</TabsContent>
			</Tabs>
		</div>
	);
}

/** StatusBanner — тоновая well-карточка состояния (апелляция / ручная проверка). */
function StatusBanner({
	tone,
	icon,
	title,
	subtitle,
}: {
	tone: "info" | "warning";
	icon: React.ReactNode;
	title: string;
	subtitle?: string;
}): React.ReactElement {
	const toneCls =
		tone === "warning"
			? "border-warning/30 bg-warning/8 text-warning"
			: "border-info/30 bg-info/8 text-info";
	return (
		<div
			className={`flex items-start gap-2.5 rounded-[var(--radius-card)] border p-3 [box-shadow:inset_0_1px_2px_rgb(var(--shadow)/0.08)] ${toneCls}`}
		>
			<span className="mt-0.5 shrink-0">{icon}</span>
			<div className="text-sm">
				<p className="font-medium">{title}</p>
				{subtitle && <p className="text-2xs opacity-80">{subtitle}</p>}
			</div>
		</div>
	);
}

function StrengthsImprovements({ essay }: { essay: EssayDetailDto }): React.ReactElement {
	const { t } = useTranslation("essays");
	const strengths = essay.strengths ?? [];
	const improvements = essay.improvements ?? [];
	return (
		<div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
			<div className="rounded-[var(--radius-card)] border border-success/25 bg-success/8 p-3.5 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.3)]">
				<h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-success">
					<IconThumbUp size={13} stroke={2} />
					{t("detail.strengths")}
				</h3>
				<ul className="space-y-1.5 text-sm text-foreground/90">
					{strengths.map((s, i) => (
						<li key={i} className="flex gap-2">
							<IconCircleCheckFilled size={14} className="mt-0.5 shrink-0 text-success" />
							<RichContent content={s} inline className="leading-relaxed" />
						</li>
					))}
				</ul>
			</div>
			<div className="rounded-[var(--radius-card)] border border-warning/25 bg-warning/8 p-3.5 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.3)]">
				<h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-warning">
					<IconTrendingUp size={13} stroke={2} />
					{t("detail.improvements")}
				</h3>
				<ul className="space-y-1.5 text-sm text-foreground/90">
					{improvements.map((s, i) => (
						<li key={i} className="flex gap-2">
							<span className="mt-0.5 shrink-0 font-semibold text-warning">→</span>
							<RichContent content={s} inline className="leading-relaxed" />
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}

function DetailSkeleton(): React.ReactElement {
	return (
		<div className={LIST_PAGE_SHELL}>
			<Skeleton className="h-8 w-2/3 max-w-md" />
			<div className="grid grid-cols-12 gap-6">
				<div className="col-span-12 space-y-3 lg:col-span-7">
					<Skeleton className="h-96 rounded-[var(--radius-panel)]" />
				</div>
				<div className="col-span-12 space-y-3 lg:col-span-5">
					<Skeleton className="h-36 rounded-[var(--radius-panel)]" />
					<Skeleton className="h-64 rounded-[var(--radius-panel)]" />
				</div>
			</div>
		</div>
	);
}
